import {
  Card,
  dateToTime,
  ErrorMessage,
  InformationCircleIcon,
  Input,
  SwitchInput,
  timeToDate,
} from "@";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import type { VerificationsSectionProps } from "./types";
import type { ProjectVerificationLimitation } from "../../types";
import {
  RepeaterAddButton,
  RepeaterClearAll,
  RepeaterEmpty,
  RepeaterItem,
} from "../RepeaterPrimitives";
import React from "react";

const defaultVerificationLimitation: ProjectVerificationLimitation = {
  minNumberOfPhotos: 1,
  maxNumberOfPhotos: 3,
  minNumberOfVideos: 0,
  maxNumberOfVideos: 0,
  maxPhotoSize: 5,
  maxVideoSize: 15,
  verificationStageNumber: 1,
  verificationStageName: "",
  smsMessage: "",
  smsSendingTime: "",
  cutoffTime: "",
};

export const VerificationsSection = ({
  register: _register,
  watch,
  setValue,
  errors,
  show,
}: VerificationsSectionProps) => {
  const limitations =
    (watch(
      "projectVerificationLimitations",
    ) as ProjectVerificationLimitation[]) || [];

  const isEnabled = watch("enableVerification") as boolean;

  const addStage = () => {
    const newStage: ProjectVerificationLimitation = {
      ...defaultVerificationLimitation,
      verificationStageNumber: limitations.length + 1,
    };
    setValue("projectVerificationLimitations", [...limitations, newStage]);
  };

  const removeStage = (index: number) => {
    const updated = limitations
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, verificationStageNumber: i + 1 }));
    setValue("projectVerificationLimitations", updated);
  };

  const updateStage = (
    index: number,
    field: keyof ProjectVerificationLimitation,
    value: string | number,
  ) => {
    const updated = limitations.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setValue("projectVerificationLimitations", updated);
  };

  const handleEnableVerification = (value: boolean) => {
    setValue("enableVerification", value);
    if (value) {
      setValue("projectVerificationLimitations", [
        defaultVerificationLimitation,
      ]);
    } else {
      setValue("projectVerificationLimitations", []);
    }
  };

  return (
    <>
      {errors?.projectVerificationLimitations && (
        <ErrorMessage value="يوجد خطأ في إعدادات التوثيق" />
      )}
      <Card
        star={isEnabled && !show}
        isOpen={true}
        className="lg:col-span-2"
        title="إعدادات التوثيق"
      >
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50/60 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex items-center gap-3">
            <span
              className={`flex size-10 items-center justify-center rounded-2xl transition ${
                isEnabled
                  ? "bg-purple-900 text-white dark:bg-purple-500"
                  : "bg-white text-gray-400 ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
              }`}
            >
              <ShieldCheckIcon className="size-5" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                تفعيل ميزة التوثيق
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                مراحل توثيق تتطلب صور أو فيديوهات لإكمال التبرع
              </p>
            </div>
          </div>
          <SwitchInput
            disabled={!!show}
            label=""
            checked={isEnabled}
            change={handleEnableVerification}
          />
        </div>

        {isEnabled && (
          <div className="mt-5 space-y-4">
            {limitations.length === 0 && !show && (
              <RepeaterEmpty
                icon={ShieldCheckIcon}
                title="لا توجد مراحل توثيق"
                description="أضف مرحلة توثيق واحدة على الأقل"
              />
            )}

            {limitations.map((limitation, index) => (
              <RepeaterItem
                key={index}
                index={index}
                label={`مرحلة التوثيق ${limitation.verificationStageNumber}`}
                show={show}
                onRemove={() => removeStage(index)}
              >
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <Input
                    label="اسم المرحلة"
                    disabled={!!show}
                    value={limitation.verificationStageName}
                    change={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateStage(
                        index,
                        "verificationStageName",
                        e.target.value,
                      )
                    }
                    error={
                      errors?.projectVerificationLimitations?.[index]
                        ?.verificationStageName
                    }
                  />

                  <div className="flex items-end gap-2 lg:col-span-2">
                    <div className="w-full">
                      <Input
                        type="text"
                        label="رسالة SMS"
                        disabled={!!show}
                        value={limitation.smsMessage}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(index, "smsMessage", e.target.value)
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.smsMessage
                        }
                      />
                    </div>
                    <Input
                      star="none"
                      dateFormat="HH:mm"
                      showTimeSelect={true}
                      showTimeOnly={true}
                      type="datepicker"
                      label="وقت الإرسال"
                      disabled={!!show}
                      value={timeToDate(limitation.smsSendingTime)}
                      change={(date: Date | null) => {
                        updateStage(index, "smsSendingTime", dateToTime(date));
                      }}
                      error={
                        errors?.projectVerificationLimitations?.[index]
                          ?.smsSendingTime
                      }
                    />
                  </div>

                  {/* Photos group */}
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/40 p-3 dark:border-gray-700 dark:bg-gray-900/30 md:col-span-2 lg:col-span-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      الصور
                    </p>
                    <div className="grid gap-3 md:grid-cols-3">
                      <Input
                        type="number"
                        min="0"
                        label="الحد الأدنى للصور"
                        disabled={!!show}
                        value={limitation.minNumberOfPhotos}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(
                            index,
                            "minNumberOfPhotos",
                            e.target.value,
                          )
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.minNumberOfPhotos
                        }
                      />
                      <Input
                        type="number"
                        min="0"
                        label="الحد الأقصى للصور"
                        disabled={!!show}
                        value={limitation.maxNumberOfPhotos}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(
                            index,
                            "maxNumberOfPhotos",
                            e.target.value,
                          )
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.maxNumberOfPhotos
                        }
                      />
                      <Input
                        type="number"
                        min="0"
                        label="حجم الصورة (MB)"
                        disabled={!!show}
                        value={limitation.maxPhotoSize}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(index, "maxPhotoSize", e.target.value)
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.maxPhotoSize
                        }
                      />
                    </div>
                  </div>

                  {/* Videos group */}
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/40 p-3 dark:border-gray-700 dark:bg-gray-900/30 md:col-span-2 lg:col-span-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      الفيديوهات
                    </p>
                    <div className="grid gap-3 md:grid-cols-3">
                      <Input
                        type="number"
                        min="0"
                        label="الحد الأدنى للفيديوهات"
                        disabled={!!show}
                        value={limitation.minNumberOfVideos}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(
                            index,
                            "minNumberOfVideos",
                            e.target.value,
                          )
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.minNumberOfVideos
                        }
                      />
                      <Input
                        type="number"
                        min="0"
                        label="الحد الأقصى للفيديوهات"
                        disabled={!!show}
                        value={limitation.maxNumberOfVideos}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(
                            index,
                            "maxNumberOfVideos",
                            e.target.value,
                          )
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.maxNumberOfVideos
                        }
                      />
                      <Input
                        type="number"
                        min="0"
                        label="حجم الفيديو (MB)"
                        disabled={!!show}
                        value={limitation.maxVideoSize}
                        change={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStage(index, "maxVideoSize", e.target.value)
                        }
                        error={
                          errors?.projectVerificationLimitations?.[index]
                            ?.maxVideoSize
                        }
                      />
                    </div>
                  </div>

                  {/* Cutoff time */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <Input
                      star="none"
                      dateFormat="HH:mm"
                      showTimeSelect={true}
                      showTimeOnly={true}
                      type="datepicker"
                      label="آخر وقت لتوثيقات اليوم الحالي"
                      disabled={!!show}
                      value={timeToDate(limitation.cutoffTime)}
                      change={(date: Date | null) => {
                        updateStage(index, "cutoffTime", dateToTime(date));
                      }}
                      error={
                        errors?.projectVerificationLimitations?.[index]
                          ?.cutoffTime
                      }
                    />
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
                      <InformationCircleIcon className="size-4" />
                      الطلبات بعد هذا الوقت سوف يكون أول توثيق لها في يوم غدًا
                    </p>
                  </div>
                </div>
              </RepeaterItem>
            ))}

            {!show && (
              <div className="space-y-3">
                <RepeaterAddButton
                  onClick={addStage}
                  label="إضافة مرحلة توثيق"
                  hint="مرحلة جديدة بقيود الصور والفيديوهات والرسائل"
                />
                {limitations.length > 1 && (
                  <div className="flex justify-end">
                    <RepeaterClearAll
                      onClick={() =>
                        setValue("projectVerificationLimitations", [])
                      }
                      label="حذف جميع المراحل"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <ErrorMessage
          className="mt-4"
          value={
            (errors.projectVerificationLimitations as { message?: string })
              ?.message
          }
        />
      </Card>
    </>
  );
};
